/**
 * Thermal label PDF generation — one label per PDF page, page-size = label-size.
 *
 * The browser print dialog dispatches each page to the OS print queue; the OS
 * + the manufacturer's printer driver handles the vendor-specific protocol
 * (Brother P-touch / Dymo / etc) when the user picks the right printer. No
 * services round-trip; no in-process thermal driver.
 *
 * For optimal output, the user picks "Actual size" (not "Fit to page") in the
 * browser print dialog and selects the thermal printer with the matching media
 * size configured.
 */

import jsPDF from 'jspdf';
import bwipjs from 'bwip-js/browser';
import type { LabelItem } from './build-sheet';
import type { ThermalLabelSpec } from './specs';
import { fitLines, fitText } from './fit-text';

const PT_PER_IN = 72;

/**
 * Type sizes for a thermal label's two text lines — vendor#1103.
 *
 * The reference keeps a higher floor than the card line: it is the string a
 * human reads back to find the card when the scanner will not cooperate, and it
 * is a fixed 15 characters, so it does not need much room. The card line is the
 * one that varies from "Pikachu · 025 · BS · LP" to a graded cert, so it is
 * given more range before characters are dropped.
 *
 * 5.5pt is the floor because a 203 dpi head puts roughly 11 dots of cap height
 * there. Below that the print is present and not readable, which is worse than
 * a visible truncation — see the proof sheet on vendor#1103.
 */
const REF_TYPE = { max: 7, min: 6 } as const;
/**
 * The card line may take a SECOND row before it loses a character — vendor#1105.
 *
 * `singleLineMin` is 6 rather than 5.5 on purpose: shrinking to the absolute
 * floor on ONE line, to avoid a second line, is the wrong trade once the second
 * line is nearly free. Measured on 2 × 1, the tallest three-line block is
 * 26.3pt against 60.5pt of usable height — the ellipsis stops being the normal
 * outcome and becomes the last resort it should always have been.
 */
const CARD_TYPE = { max: 7, min: 5.5, singleLineMin: 6, maxLines: 2 } as const;

/** Leading multiplier. Matches jsPDF's own default line factor closely enough
 *  that a label laid out here and one measured there agree. */
const LEADING = 1.25;

export function buildThermalLabelPDF(items: LabelItem[], spec: ThermalLabelSpec): jsPDF {
  const widthPt = spec.widthIn * PT_PER_IN;
  const heightPt = spec.heightIn * PT_PER_IN;
  const marginPt = spec.marginIn * PT_PER_IN;

  // jsPDF auto-orients when format[0] > format[1]; setting orientation
  // explicitly keeps pageSize.width == widthPt regardless of aspect ratio.
  const orientation = widthPt >= heightPt ? 'landscape' : 'portrait';
  const doc = new jsPDF({ unit: 'pt', orientation, format: [widthPt, heightPt] });

  // Wide DataMatrix labels (Dymo / Brother) use a side-by-side layout: the
  // square code anchored left at full label height, text in the right column.
  // This makes the code as large as the media allows — the whole point of the
  // DataMatrix variant is maximum module size for handheld phone scanning.
  // Square media (round 1") and the Code 128 variants keep the stacked layout.
  const isWideDataMatrix = spec.symbology === 'datamatrix' && widthPt / heightPt >= 1.4;
  const wideCodeSize = heightPt - marginPt * 2;
  const wideTextX = marginPt + wideCodeSize + marginPt;

  items.forEach((item, idx) => {
    if (idx > 0) doc.addPage([widthPt, heightPt], orientation);

    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      try {
        if (spec.symbology === 'datamatrix') {
          bwipjs.toCanvas(canvas, {
            bcid: 'datamatrix',
            text: item.barcode,
            scale: 4,
            includetext: false,
          });
          const dataUrl = canvas.toDataURL('image/png');
          if (isWideDataMatrix) {
            // Square sized to the full usable height, anchored left.
            doc.addImage(dataUrl, 'PNG', marginPt, marginPt, wideCodeSize, wideCodeSize);
          } else {
            // Square code centered horizontally, ~55% of the smaller dimension.
            const codeSize = Math.min(widthPt, heightPt) * 0.55;
            doc.addImage(dataUrl, 'PNG', (widthPt - codeSize) / 2, marginPt, codeSize, codeSize);
          }
        } else {
          bwipjs.toCanvas(canvas, {
            bcid: 'code128',
            text: item.barcode,
            scale: 2,
            height: 12,
            includetext: false,
          });
          const dataUrl = canvas.toDataURL('image/png');
          // Stretch barcode across the usable width, ~60% of label height.
          doc.addImage(dataUrl, 'PNG', marginPt, marginPt, widthPt - marginPt * 2, heightPt * 0.6);
        }
      } catch {
        // Fall through to text-only if bwip-js rejects the payload.
      }
    }

    /*
     * TEXT IS FITTED, NEVER WRAPPED — vendor#1103.
     *
     * Both lines used to be drawn at fixed offsets 10pt apart, each with
     * jsPDF's `{ maxWidth }`. That option **wraps**; it does not truncate. So a
     * line too wide for the column produced a second line exactly where the
     * next line was drawn, and the two overprinted — which is how a printed
     * label came back with the card name running through the reference above
     * it. On the stacked layout it was worse: `line2` sits 4pt off the bottom
     * edge, so its wrapped remainder left the label entirely.
     *
     * A label line is a single line by construction. There is one row for it
     * and no reflow, so the text is made to fit instead.
     */
    if (isWideDataMatrix) {
      const textMaxWidth = widthPt - wideTextX - marginPt;
      const ref = item.line1 ? fitText(doc, item.line1, textMaxWidth, REF_TYPE) : null;
      const card = item.line2 ? fitLines(doc, item.line2, textMaxWidth, CARD_TYPE) : null;

      // The block is centred on the label rather than pinned to fixed offsets,
      // so a two-line label sits centred and a three-line one stays balanced.
      const refH = ref ? ref.fontSize * LEADING : 0;
      const cardH = card ? card.fontSize * LEADING * card.lines.length : 0;
      let y =
        heightPt / 2 -
        (refH + cardH) / 2 +
        (ref ? ref.fontSize * 0.8 : card ? card.fontSize * 0.8 : 0);

      if (ref) {
        doc.setFontSize(ref.fontSize);
        doc.text(ref.text, wideTextX, y);
        y += refH;
      }
      if (card) {
        doc.setFontSize(card.fontSize);
        for (const line of card.lines) {
          doc.text(line, wideTextX, y);
          y += card.fontSize * LEADING;
        }
      }
    } else {
      const stackedMaxWidth = widthPt - marginPt * 2;
      const ref = item.line1 ? fitText(doc, item.line1, stackedMaxWidth, REF_TYPE) : null;
      const card = item.line2 ? fitLines(doc, item.line2, stackedMaxWidth, CARD_TYPE) : null;

      // Bottom-anchored, and the LAST card line is the last baseline rather than
      // a fixed +1pt past the margin — which is what let a wrapped remainder run
      // off the media. Drawn upwards, so a second line grows INTO the label
      // rather than off the bottom of it.
      let y = heightPt - marginPt - (card ? card.fontSize * 0.25 : 0);
      if (card) {
        doc.setFontSize(card.fontSize);
        for (const line of [...card.lines].reverse()) {
          doc.text(line, widthPt / 2, y, { align: 'center' });
          y -= card.fontSize * LEADING;
        }
      }
      if (ref) {
        doc.setFontSize(ref.fontSize);
        doc.text(ref.text, widthPt / 2, y, { align: 'center' });
      }
    }
    doc.setFontSize(7);
  });

  return doc;
}
